/**
 * Placement of the Hunt entry card in the /jobs feed (`huntCard` knob of the
 * `feed-rank-policy` payload, src/lib/personalization/rank-policy.ts).
 *
 * Why (research PR #7640): a Hunt chat card is worth ~10x a feed card (21% of
 * Hunt cards lead to an outbound apply), so one feed slot spent on a Hunt
 * entry is a bet the experiment can price.
 *
 * The card is never a job. It is inserted at render time, after ranking, so
 * it takes no rank, never counts toward the local-share cap, and is not an
 * item of `feed_slate_rendered` (the slate records `hunt_card_slot` instead).
 * Pure: the client feed list imports it.
 */

/** Highest slot the payload may name (one feed page). */
export const HUNT_CARD_MAX_SLOT = 20;

export type FeedListEntry<T> =
	| {
			kind: "job";
			job: T /** 0-based index among the jobs. */;
			jobIndex: number;
	  }
	| { kind: "hunt_card"; slot: number };

/**
 * The slot the card takes on this page, or null: page 1 only, a whole slot
 * within the page's job count (a short page never grows a trailing card).
 */
export function huntCardSlotForPage(
	slot: number | null | undefined,
	page: number,
	jobCount: number,
): number | null {
	if (page !== 1) return null;
	if (typeof slot !== "number" || !Number.isInteger(slot)) return null;
	if (slot < 1 || slot > HUNT_CARD_MAX_SLOT || slot > jobCount) return null;
	return slot;
}

/**
 * The visual list: every job in order, with the Hunt card at 1-indexed
 * visual `slot` (so `slot - 1` jobs precede it). A null slot = jobs only.
 */
export function withHuntCard<T>(
	jobs: readonly T[],
	slot: number | null,
): FeedListEntry<T>[] {
	const out: FeedListEntry<T>[] = jobs.map((job, jobIndex) => ({
		kind: "job",
		job,
		jobIndex,
	}));
	if (slot === null || slot < 1 || slot > jobs.length) return out;
	out.splice(slot - 1, 0, { kind: "hunt_card", slot });
	return out;
}
